// Router-level auth gate for the two portals.
//
// DESIGN: auth is gated on `shouldUseSupabase()` — the SAME switch the data
// layer uses (see ../../data). When Supabase is NOT configured (local dev with
// no env, and the whole test suite) the gate renders the portal shell directly,
// preserving the built-in demo-auth stub so nothing about the offline/reviewable
// experience — or the existing tests — changes. When Supabase IS configured
// (the deployed site, env set at build) the gate requires a real magic-link
// session before the app shell renders, and sign-out really ends the session.
//
// The portal shells (ServicePortal/AdminPortal) still own the demo stub for the
// unconfigured path; in the configured path the gate drives them via the
// optional `accountEmail`/`accountId`/`onSignOut` props so their sign-out button
// and account label reflect the real session.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useComfort } from "../ComfortContext";
import { useSession, verifyEmailCode } from "../../lib/auth";
import { getSupabaseClient } from "../../lib/supabase";
import { shouldUseSupabase } from "../../data";
import { ServicePortal } from "../../components/ServicePortal";
import { AdminPortal } from "../../components/AdminPortal";
import { ServiceSignIn } from "./ServiceSignIn";
import { AdminSignIn } from "./AdminSignIn";
import { AdminNotOrg } from "./AdminNotOrg";
import { CreateOrganisation } from "./CreateOrganisation";
import { LinkSent } from "./LinkSent";
import { OrgProvider, useOrg } from "../admin/OrgContext";
import type { Locale } from "../../i18n/dictionary";
import {
  sendMagicLink,
  serviceRedirectUrl,
  adminRedirectUrl,
} from "./sendMagicLink";

/** Ends the real Supabase session; onAuthStateChange then flips the gate. */
async function portalSignOut(): Promise<void> {
  try {
    await getSupabaseClient().auth.signOut();
  } catch {
    // Best-effort — if sign-out fails the session simply persists.
  }
}

/** Minimal centered spinner shown while the initial session resolves. */
function AuthSplash() {
  return (
    <div
      className="zp-root"
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "var(--bg)",
      }}
    >
      <span
        aria-label="Loading"
        style={{
          width: 28,
          height: 28,
          border: "3px solid var(--line-strong)",
          borderTopColor: "var(--brand)",
          borderRadius: "50%",
          animation: "zspin .8s linear infinite",
        }}
      />
    </div>
  );
}

// ---- Service portal gate ----------------------------------------------------

export function ServiceAuthGate() {
  return shouldUseSupabase() ? <ServiceAuthGateLive /> : <ServicePortal />;
}

function ServiceAuthGateLive() {
  const { locale, setLocale } = useComfort();
  const session = useSession();
  const [sentTo, setSentTo] = useState<string | null>(null);

  if (session.status === "loading") return <AuthSplash />;

  if (session.status === "signed_out") {
    if (sentTo) {
      return (
        <LinkSent
          locale={locale}
          email={sentTo}
          onResend={() => void sendMagicLink(sentTo, serviceRedirectUrl())}
          onDifferentEmail={() => setSentTo(null)}
          onVerifyCode={async (code) => !(await verifyEmailCode(sentTo, code)).error}
        />
      );
    }
    return (
      <ServiceSignIn locale={locale} setLocale={setLocale} onSent={setSentTo} />
    );
  }

  return (
    <ServicePortal
      accountEmail={session.email}
      accountId={session.userId}
      onSignOut={() => void portalSignOut()}
    />
  );
}

// ---- Admin portal gate ------------------------------------------------------

export function AdminAuthGate() {
  return shouldUseSupabase() ? <AdminAuthGateLive /> : <AdminPortal />;
}

function AdminAuthGateLive() {
  const { locale, setLocale } = useComfort();
  const session = useSession();
  const [sentTo, setSentTo] = useState<string | null>(null);

  if (session.status === "loading") return <AuthSplash />;

  if (session.status === "signed_out") {
    if (sentTo) {
      return (
        <LinkSent
          locale={locale}
          email={sentTo}
          onResend={() => void sendMagicLink(sentTo, adminRedirectUrl())}
          onDifferentEmail={() => setSentTo(null)}
          onVerifyCode={async (code) => !(await verifyEmailCode(sentTo, code)).error}
        />
      );
    }
    return (
      <AdminSignIn locale={locale} setLocale={setLocale} onSent={setSentTo} />
    );
  }

  // Authenticated — now AUTHORIZE. Being signed in says nothing about belonging
  // to an organisation, let alone administering one.
  return (
    <OrgProvider accountId={session.userId}>
      <AdminOrgGate
        locale={locale}
        email={session.email}
        accountId={session.userId}
        onSignOut={() => void portalSignOut()}
      />
    </OrgProvider>
  );
}

/**
 * Second half of the admin gate: decides what a signed-in adult actually sees,
 * based on the organisation resolved by {@link OrgProvider}.
 *
 * Splitting this out of AdminAuthGateLive is what lets it call useOrg() — a
 * hook cannot read a provider its own component renders.
 */
function AdminOrgGate({
  locale,
  email,
  accountId,
  onSignOut,
}: {
  locale: Locale;
  email?: string;
  accountId?: string;
  onSignOut: () => void;
}) {
  const org = useOrg();

  if (org.status === "loading") return <AuthSplash />;

  if (org.status === "error") {
    return (
      <OrgLoadFailed message={org.error} onRetry={org.refresh} onSignOut={onSignOut} />
    );
  }

  // No organisation yet: offer to create one rather than dead-ending. This is
  // the ONLY route into the admin portal for a new customer.
  if (org.status === "none") {
    return (
      <CreateOrganisation
        locale={locale}
        email={email}
        onCreated={org.refresh}
        onSignOut={onSignOut}
      />
    );
  }

  // In an organisation, but as a plain member. The admin portal manages seats,
  // members and licences — none of which they may touch — so send them to the
  // "ask your admin" screen instead of a shell whose every write returns 42501.
  if (org.status === "not_admin") {
    return <AdminNotOrg locale={locale} onBack={onSignOut} />;
  }

  return (
    <AdminPortal
      accountEmail={email}
      accountId={accountId}
      orgName={org.orgName}
      onSignOut={onSignOut}
    />
  );
}

/**
 * The org lookup itself failed — a network blip, or RLS/schema drift. Offering a
 * retry matters here: the previous behaviour was to render the shell anyway, so
 * a transient failure looked like "your organisation is empty".
 */
function OrgLoadFailed({
  message,
  onRetry,
  onSignOut,
}: {
  message: string;
  onRetry: () => void;
  onSignOut: () => void;
}) {
  return (
    <div
      className="zp-root"
      style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 32, background: "var(--bg)", fontFamily: "var(--font-body)", textAlign: "center" }}
    >
      <div style={{ maxWidth: 440 }}>
        <h1 style={{ fontSize: 25, color: "var(--ink-deep)", margin: 0 }}>
          We couldn't load your organisation
        </h1>
        <p style={{ color: "var(--ink-soft)", fontSize: 16, lineHeight: 1.65, marginTop: 12 }}>
          This is usually a connection hiccup. Nothing has changed on your account.
        </p>
        {message ? (
          <p style={{ marginTop: 14, padding: "10px 13px", background: "var(--peach-tint)", color: "var(--peach-deep)", borderRadius: "var(--r-md)", fontSize: 14, wordBreak: "break-word" }}>
            {message}
          </p>
        ) : null}
        <button
          onClick={onRetry}
          style={{ marginTop: 20, minHeight: 52, padding: "0 30px", border: "none", background: "var(--brand)", color: "#fff", borderRadius: "var(--r-pill)", fontFamily: "var(--font-body)", fontWeight: 700, fontSize: 16, cursor: "pointer" }}
        >
          Try again
        </button>
        <button
          onClick={onSignOut}
          style={{ display: "block", margin: "12px auto 0", minHeight: 44, border: "none", background: "transparent", color: "var(--ink-soft)", fontFamily: "var(--font-body)", fontWeight: 600, fontSize: 15, cursor: "pointer" }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

// ---- Magic-link callback ----------------------------------------------------

/**
 * Landing route for the magic-link email. The Supabase client parses the session
 * out of the URL (detectSessionInUrl) on load; this component waits for the
 * session to resolve, then redirects into the portal (which the gate now renders
 * as signed-in). `to` is "/" for the service portal, "/admin" for admin.
 */
export function AuthCallback({ to }: { to: string }) {
  return shouldUseSupabase() ? (
    <AuthCallbackLive to={to} />
  ) : (
    <AuthCallbackRedirect to={to} />
  );
}

function AuthCallbackLive({ to }: { to: string }) {
  const session = useSession();
  const navigate = useNavigate();
  useEffect(() => {
    if (session.status !== "loading") navigate(to, { replace: true });
  }, [session.status, to, navigate]);
  return <AuthSplash />;
}

function AuthCallbackRedirect({ to }: { to: string }) {
  const navigate = useNavigate();
  useEffect(() => {
    navigate(to, { replace: true });
  }, [to, navigate]);
  return <AuthSplash />;
}
